import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Category} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/category.types';
import {TreeNavigationItem} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tree_navigation_item.types';
import {useTranslation} from '@aboutyou/i18n';
import {originClick} from '@aboutyou/legacy-tracking-sdk';
import {useTrackFeature, useTrackOriginClick} from '@aboutyou/tracking';
import {
  COLORS,
  GenericIconName,
  Icon,
  NavigationTree as NavigationTreeUIComponent,
  Text,
  TreeNodeProps,
  UnstyledLink,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React from 'react';
import sc from 'styled-components';
import {useIsSelectedDiscountAppOnly} from '../../backend-services/Campaigns/SelectedDiscounts/useSelectedDiscount';
import {useSelectedDiscountColor} from '../../backend-services/Campaigns/SelectedDiscounts/useSelectedDiscountColor';
import {useSelectedDiscountUrl} from '../../backend-services/Campaigns/SelectedDiscounts/useSelectedDiscountUrl';
import {useMergedPath} from '../../shared-deprecated/useMergedPath';

export interface NavigationTreeProps {
  className?: string;
  testId: string;
  trackOriginKey: originClick;
  excludeUrlParams?: string[];
  categoryTree: ExtendedTreeItem[];
  useButtonLink?: boolean;
  hideAllDealItems?: boolean;
}

export interface ExtendedTreeItem extends TreeNavigationItem {
  category: Category & {
    targetPath?: string;
  };
}

export const NavigationTree: React.FC<NavigationTreeProps> = ({
  categoryTree,
  trackOriginKey,
  excludeUrlParams,
  useButtonLink,
  hideAllDealItems,
  ...rest
}) => {
  const treeNodes = useNormalizeNodes({
    categoryTree,
    excludeUrlParams,
  });
  const trackFeature = useTrackFeature();

  const trackOrigin = useTrackOriginClick();
  const handleTrackOrigin = () => {
    trackOrigin(trackOriginKey)();
    trackFeature({
      eventType: 'click',
      label: 'sub_category',
      name: 'sub_category',
    });
  };

  return (
    <NavigationTreeUIComponent
      nodes={treeNodes}
      trackOrigin={handleTrackOrigin}
      useButtonLink={useButtonLink}
      allDealItems={hideAllDealItems ? null : <AllDealItems />}
      {...rest}
    />
  );
};

const getIcon = (
  node: ExtendedTreeItem,
  showCouponsIcon: boolean,
):
  | {
      icon: GenericIconName;
      color: {hex: string};
    }
  | undefined => {
  if (showCouponsIcon && node.reductionType?.$case === 'couponReduction') {
    return {
      icon: GenericIconName.CouponNav,
      color: {hex: COLORS.PrimaryCoupon},
    };
  }
  if (node.reductionType?.$case === 'discountReduction') {
    return {
      ...node.reductionType?.discountReduction,
      icon: GenericIconName.Sale,
    };
  }
};

const useNormalizeNodes = ({
  categoryTree,
  excludeUrlParams = [],
}: {
  categoryTree: Array<TreeNavigationItem | ExtendedTreeItem>;
  excludeUrlParams?: string[];
}): Array<TreeNodeProps> => {
  const getMergedPath = useMergedPath();
  const {isActive: showCouponsIcon} = useIsFeatureActive('high_tier_coupons');

  const getNodePath = (node: ExtendedTreeItem) => {
    if (node.category.targetPath || !node.productCount) {
      return node.category.path;
    }

    if (node.link.url && !excludeUrlParams.includes('sort')) {
      return getMergedPath({
        path: node.link.url,
        config: {includedUrlParams: new Set(['sort'])},
      });
    }

    return getMergedPath({
      path: node.category.path,
      config: {excludeUrlParams: new Set(['path', ...excludeUrlParams])},
    });
  };

  const normalizeNode = (node: ExtendedTreeItem): TreeNodeProps => ({
    active: node.active,
    current: node.current,
    disabled: Boolean(!node.productCount),
    textColor: !node.productCount ? COLORS.Grey2 : node.textColor,
    id: node.category.id,
    label: node.category.name,
    path: getNodePath(node),
    badges: node.badges,
    children: node.children.map(normalizeNode),
    targetPath: node.category.targetPath
      ? getMergedPath({
          path: node.category.targetPath,
          config: {excludeUrlParams: new Set(['path', ...excludeUrlParams])},
        })
      : undefined,
    icon: getIcon(node, showCouponsIcon),
  });

  return categoryTree.map(normalizeNode);
};

const StyledSaleIcon = linariaStyled(Icon)`
  margin-left: 4px;
`;

const AllDealItems = () => {
  const {t} = useTranslation();

  const discountUrl = useSelectedDiscountUrl();
  const discountColor = useSelectedDiscountColor();
  const isSelectedDiscountAppOnly = useIsSelectedDiscountAppOnly();

  if (!discountUrl || isSelectedDiscountAppOnly) {
    return null;
  }

  return (
    <ListItem>
      <StyledLink to={discountUrl.url ?? ''} aria-label={undefined}>
        <AllDealItemsLabel
          size={'px18'}
          weight="mediumBold"
          $textColor={discountColor!}
        >
          {t('All Deal Items')}
        </AllDealItemsLabel>
        <StyledSaleIcon
          fill={discountColor!}
          size="XS"
          asset={GenericIconName.Sale}
        />
      </StyledLink>
    </ListItem>
  );
};

const AllDealItemsLabel = sc(Text)<{
  $textColor?: string;
}>`
  color: ${({$textColor = 'black'}) => $textColor};
`;

const StyledLink = sc(UnstyledLink)`
  display: flex;
  align-items: center;
  padding: 6px 0;
`;

const ListItem = sc.li`
  width: 100%;
`;
